import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { scaleBand } from '@visx/scale';
import { getParams, getMonth } from '../../../../Utils/Functions';
import { activeLabelColor } from '../../../../Assets/js/constant';

let tooltipTimeout;

const TopChart = ({
  summaryData,
  updateFilter,
  selectedTopLabels,
  selectedTopItems,
  selectedLeftItems,
  selectedRightItems,
  choice,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [AchievedPercent, setAchievedPercent] = useState(0);

  const _onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', _onResize);
    return () => window.removeEventListener('resize', _onResize);
  }, [_onResize]);

  const _prepareData = useCallback(() => {
    let selectedMonths = selectedRightItems;
    let dictData = {};
    let newData = [];
    let _positive = 0,
      _allachived = 0;
    let total = {};
    total['key'] = 'Total';
    total['value'] = {};

    if (summaryData.length === 0) return;

    summaryData.forEach((item) => {
      let month = getMonth(item.Date);
      if (selectedMonths.indexOf(month) > -1) {
        let key = '';
        let Outcome = '';

        if (item.DesiredOutcome === 'Less') {
          if (parseFloat(item.Result) <= parseFloat(item.Target))
            Outcome = 'Achieved';
          else if (
            parseFloat(item.Result) > parseFloat(item.Target) &&
            parseFloat(item.Result) <=
              parseFloat(item.Target) + parseFloat(item.Tolerance)
          )
            Outcome = 'Neutral';
          else if (
            parseFloat(item.Result) >=
            parseFloat(item.Target) + parseFloat(item.Tolerance)
          )
            Outcome = 'Not Achieved';
        } else if (item.DesiredOutcome === 'Greater') {
          if (parseFloat(item.Result) >= parseFloat(item.Target))
            Outcome = 'Achieved';
          else if (
            parseFloat(item.Result) >=
              parseFloat(item.Target) - parseFloat(item.Tolerance) &&
            parseFloat(item.Result) < parseFloat(item.Target)
          )
            Outcome = 'Neutral';
          else if (
            parseFloat(item.Result) <=
            parseFloat(item.Target) - parseFloat(item.Tolerance)
          )
            Outcome = 'Not Achieved';
        }

        if (choice === 'Group') {
          key = item.Group;
        } else if (choice === 'Result') {
          if (Outcome === 'Neutral') key = 'Neutral';
          else if (Outcome === 'Achieved') key = 'Good';
          else key = 'Bad';
        } else if (choice === 'Importance') {
          key = item.Priority;
        }

        if (dictData[key] === undefined) {
          dictData[key] = {};
          dictData[key]['key'] = key;
          dictData[key]['month'] = month;
          dictData[key]['value'] = {};
        }

        if (dictData[key]['value'][Outcome] === undefined) {
          dictData[key]['value'][Outcome] = {};
          dictData[key]['value'][Outcome]['idx'] =
            Outcome === 'Achieved' ? 1 : Outcome === 'Not Achieved' ? -1 : 0;
          dictData[key]['value'][Outcome]['data'] = 1;
        } else {
          dictData[key]['value'][Outcome]['data']++;
        }

        if (total['value'][Outcome] === undefined) {
          total['value'][Outcome] = {};
          total['value'][Outcome]['idx'] =
            Outcome === 'Achieved' ? 1 : Outcome === 'Not Achieved' ? -1 : 0;
          total['value'][Outcome]['data'] = 1;
        } else {
          total['value'][Outcome]['data']++;
        }

        if (Outcome === 'Achieved' || Outcome === 'Neutral') _positive++;
        _allachived++;
      }
    });

    Object.keys(dictData).map((key) => newData.push(dictData[key]));

    newData.sort((a, b) => {
      if (a.key.toLowerCase() > b.key.toLowerCase()) return -1;
      if (a.key.toLowerCase() < b.key.toLowerCase()) return 1;
      return 0;
    });
    newData.push(total);
    
    setData(newData);
    setAchievedPercent(_allachived ? Math.round((_positive / _allachived) * 100) : 0);
  }, [summaryData, selectedRightItems, choice]);

  useEffect(() => {
    _prepareData();
  }, [summaryData, selectedLeftItems, selectedRightItems, choice, selectedTopLabels, _prepareData]);

  const _deSelectAll = useCallback(() => {
    let _selectedTopItems = [],
      _selectedTopLabels = [];

    data.forEach((d) => {
      _selectedTopItems.push({ key: d.key });
      _selectedTopLabels.push(d.key);
    });

    setHoverBar(null);
    setSelectedBars([]);

    updateFilter({
      selectedTopLabels: _selectedTopLabels,
      selectedTopItems: _selectedTopItems,
      selectedLeftItems: [],
    });
  }, [data, updateFilter]);

  const _showTooltip = useCallback((event, item, subkey, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let tooltipData = {},
      top,
      left;

    if (hoverBar) hoverBar.classList.remove('barHover');
    event.target.classList.add('barHover');
    setHoverBar(event.target);

    top = event.clientY - 70;
    left = event.nativeEvent.layerX + 50;
    tooltipData = item;

    tooltipData['isBar'] = isBar;
    tooltipData['subKey'] = subkey;

    showTooltip({
      tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const _hideTooltip = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  const _getColor = useCallback((item, value) => {
    let activeColor = 'black';
    let disableColor = 'grey';
    if (value === 'Achieved') {
      activeColor = '#8dc63f';
      disableColor = '#e0ebd2';
    } else if (value === 'Not Achieved') {
      activeColor = '#868686';
      disableColor = '#dfdfdf';
    } else {
      activeColor = '#e9f323';
      disableColor = '#f1f3cd';
    }

    if (selectedTopItems.length === 0 && selectedTopLabels.length === 0) {
      return activeColor;
    }
    if (selectedTopLabels.indexOf(item.key) > -1) return activeColor;

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (selectedTopItems[i].key === item.key) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedTopItems, selectedTopLabels]);

  const handleOptionChange = useCallback((changeEvent) => {
    updateFilter({
      choice: changeEvent.target.value,
      selectedTopItems: [],
      selectedTopLabels: [],
    });
  }, [updateFilter]);

  const _handleLabel = useCallback((event, key) => {
    let _selectedTopItems, _selectedBars, _selectedTopLabels;

    let index = NaN;
    for (let i = 0; i < selectedTopLabels.length; i++) {
      if (selectedTopLabels[i] === key) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopLabels = selectedTopLabels.slice();
      _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedTopItems.push({ key: key });
        _selectedTopLabels.push(key);
      } else {
        _selectedTopItems.splice(index, 1);
        _selectedTopLabels.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];
      _selectedTopLabels = [];
      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && selectedTopLabels.length === 1) {
        _selectedTopItems = [];
        _selectedTopLabels = [];
      } else {
        _selectedTopItems = [{ key: key }];
        _selectedTopLabels = [key];
      }
    }

    setSelectedBars(_selectedBars);

    updateFilter({
      selectedTopLabels: _selectedTopLabels,
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedBars, selectedTopItems, selectedTopLabels, updateFilter]);

  const _handleBar = useCallback((event, item, value) => {
    let _selectedBars, _selectedTopLabels, _selectedTopItems;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();
      _selectedTopLabels = selectedTopLabels.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopItems.push({ key: item.key, value: value });
        _selectedTopLabels.push(item.key);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopItems.splice(index, 1);
        _selectedTopLabels.splice(index, 1);
      }
    } else {
      _selectedTopLabels = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedBars = [];
        _selectedTopLabels = [];
      } else {
        _selectedTopItems = [{ key: item.key, value: value }];
        _selectedTopLabels = [item.key];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);

    updateFilter({
      selectedTopLabels: _selectedTopLabels,
      selectedTopItems: _selectedTopItems,
      selectedLeftItems: [],
    });
  }, [selectedBars, selectedTopItems, selectedTopLabels, updateFilter]);

  const width = window.innerWidth - 32;
  const height = (window.innerHeight - 30) / 4;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: 0,
  };

  const { pageYOffset, yMax } = getParams(window, width, height, margin);

  let leftWidth = width - 140 > 0 ? width - 140 : 0;
  let rightWidth = 140;

  // scales
  let keySelector = (d) => d.key;
  let xScale = scaleBand()
    .domain(data.map(keySelector))
    .rangeRound([0, leftWidth])
    .padding(0.2);

  return (
    <div>
      <div style={{ display: 'inline-block', width: '100%' }}>
        <div style={{ width: leftWidth, float: 'left' }}>
          <div
            className="subtitle"
            style={{ height: 33, display: 'flex', alignItems: 'center' }}
          >
            Key Performance Indicators
          </div>
          <svg width={leftWidth} height={height}>
            <Group top={margin.top} left={margin.left}>
              <rect
                x={0}
                y={0}
                width={leftWidth}
                height={height}
                fill="transparent"
                onClick={_deSelectAll}
              />
              {data.map((item, index) => {
                let left = 0,
                  top = 0;
                let width = 0,
                  height = 0;
                let totalKPIs = 0;
                let achieved = [];
                Object.keys(item.value).forEach((kpis) => {
                  totalKPIs += item.value[kpis].data;
                  achieved.push({
                    key: kpis,
                    idx: item.value[kpis].idx,
                    data: item.value[kpis].data,
                  });
                });

                left =
                  (xScale.paddingInner() * xScale.step()) / 2 +
                  index * xScale.step() +
                  15;
                width =
                  xScale.step() - (xScale.paddingInner() * xScale.step()) / 2;

                let renderItems = [];
                achieved.sort((a, b) => b.idx - a.idx);
                achieved.forEach((value, inx) => {
                  height = (value.data / totalKPIs) * yMax;
                  renderItems.push(
                    <rect
                      key={`bar-chart-${index}-${inx}`}
                      x={left}
                      y={top}
                      height={height}
                      width={width}
                      fill={_getColor(item, value.key)}
                      onClick={(event) => _handleBar(event, item, value.key)}
                      onMouseLeave={_hideTooltip}
                      onMouseMove={(event) =>
                        _showTooltip(event, item, value.key)
                      }
                      onTouchEnd={_hideTooltip}
                      onTouchMove={(event) =>
                        _showTooltip(event, item, value.key)
                      }
                    />,
                  );
                  top += height;
                });

                return (
                  <Group key={`bar-group-${index}`}>{renderItems}</Group>
                );
              })}

              {data.map((item, index) => {
                return (
                  <rect
                    key={index}
                    x={
                      (xScale.paddingInner() * xScale.step()) / 2 +
                      index * xScale.step()
                    }
                    y={yMax}
                    width={
                      (leftWidth -
                        (xScale.paddingInner() * xScale.step()) / 2) /
                      data.length
                    }
                    height={margin.bottom}
                    fill="transparent"
                    onClick={(event) => _handleLabel(event, item.key)}
                  />
                );
              })}

              <AxisBottom
                hideTicks={true}
                numTicks={6}
                scale={xScale}
                top={yMax}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '-0.5em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text
                    {...tickProps}
                    fill={
                      selectedTopLabels.indexOf(formattedValue) > -1
                        ? activeLabelColor
                        : 'black'
                    }
                    style={{
                      fontWeight: 700,
                    }}
                    onClick={(event) =>
                      _handleLabel(event, formattedValue)
                    }
                  >
                    {formattedValue}
                  </text>
                )}
              />
            </Group>
          </svg>
        </div>
        <div style={{ paddingTop: 4, width: rightWidth, float: 'right' }}>
          <div
            className="subtitle"
            style={{ height: 33, display: 'flex', alignItems: 'center' }}
          >
            {AchievedPercent}% Performance
          </div>
          <div style={{ height: (height / 5) * 2 }}>
            <div
              className="subtitle"
              style={{ height: 33, display: 'flex', alignItems: 'center' }}
            >
              KPI Choice
            </div>
            <form style={{ marginLeft: -4 }}>
              <div className="radio">
                <label style={{
                  fontFamily: 'Verdana',
                  fontSize: '9pt',
                }}>
                  <input
                    type="radio"
                    value="Group"
                    checked={choice === 'Group'}
                    onChange={handleOptionChange}
                  />
                  Group
                </label>
              </div>
              <div className="radio">
                <label style={{
                  fontFamily: 'Verdana',
                  fontSize: '9pt',
                }}>
                  <input
                    type="radio"
                    value="Result"
                    checked={choice === 'Result'}
                    onChange={handleOptionChange}
                  />
                  Result
                </label>
              </div>
              <div className="radio">
                <label style={{
                  fontFamily: 'Verdana',
                  fontSize: '9pt',
                }}>
                  <input
                    type="radio"
                    value="Importance"
                    checked={choice === 'Importance'}
                    onChange={handleOptionChange}
                  />
                  Importance
                </label>
              </div>
            </form>
          </div>
          <div>
            <div
              className="subtitle"
              style={{ height: 33, display: 'flex', alignItems: 'center' }}
            >
              Legend
            </div>
            <svg width={100}>
              <Group left={margin.left}>
                <rect
                  x={0}
                  y={0}
                  width={10}
                  height={10}
                  fill="#8dc63f"
                  stroke="black"
                  strokeWidth={0.5}
                />
                <text x={20} y={10} style={{
                  fontFamily: 'Verdana',
                  fontSize: '9pt',
                }}>
                  {' '}
                  Achieved{' '}
                </text>
                <rect
                  x={0}
                  y={20}
                  width={10}
                  height={10}
                  fill="#e9f323"
                  stroke="black"
                  strokeWidth={0.5}
                />
                <text x={20} y={30} style={{
                  fontFamily: 'Verdana',
                  fontSize: '9pt',
                }}>
                  {' '}
                  Neutral{' '}
                </text>
                <rect
                  x={0}
                  y={40}
                  width={10}
                  height={10}
                  fill="#868686"
                  stroke="black"
                  strokeWidth={0.5}
                />
                <text x={20} y={50} style={{
                  fontFamily: 'Verdana',
                  fontSize: '9pt',
                }}>
                  {' '}
                  Not Achieved{' '}
                </text>
              </Group>
            </svg>
          </div>
        </div>
      </div>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + pageYOffset}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="bkg-white color-black">
              <div className="pdv-10">
                <strong>{tooltipData.key}</strong>
              </div>
              <div className="ft-12">
                Outcome: <strong>{tooltipData.subKey}</strong>
              </div>
              <div className="ft-12">
                <strong>
                  {tooltipData.value[tooltipData.subKey].data} KPIs
                  contributing {tooltipData.value[tooltipData.subKey].data}{' '}
                  points
                </strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  updateFilter: PropTypes.func.isRequired,
  selectedTopLabels: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedLeftItems: PropTypes.array.isRequired,
  selectedRightItems: PropTypes.array.isRequired,
  choice: PropTypes.string.isRequired,
};

export default TopChart;
